import React, { useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import formatMessage from 'format-message'
import DrawerSearch from './DrawerSearch'
import DrawerGlossary from './DrawerGlossary'
import DrawerSettings from './DrawerSettings'
import { DrawerNotebook } from './notebook_tool/DrawerNotebook'
import DrawerContactUs from './DrawerContactUs'
import {
  createAIToolsElement,
  validateAIToolsContent,
} from '../utils/aiToolsLoader'
import DrawerHeader from './DrawerHeader'
import { normalizeSearchIdentity } from '../utils/searchHistory'

export const DrawerContainer = ({
  userId: hostUserId,
  coursenum,
  pageId,
  recentPageIds,
  upcomingAssessments,
  userEmail,
  assistantEnabled,
}) => {
  const userId = normalizeSearchIdentity(hostUserId)
  const identityRef = useRef({ userId, coursenum })
  identityRef.current = { userId, coursenum }
  const [activeDrawer, setActiveDrawer] = React.useState(null)
  const aiToolsContainerRef = useRef(null)
  const aiToolsElementRef = useRef(null)
  const isMountedRef = useRef(true)
  const [aiToolsCreated, setAiToolsCreated] = React.useState(false)
  const [aiToolsValidated, setAiToolsValidated] = React.useState(false)
  const [showTimeoutMessage, setShowTimeoutMessage] = React.useState(false)
  const [retryCount, setRetryCount] = React.useState(0)
  const [isLoading, setIsLoading] = React.useState(true)
  const isCreatingRef = useRef(false)
  const loadingTimeoutRef = useRef(null)
  const retryTimeoutRef = useRef(null)
  const validationTimeoutRef = useRef(null)

  const qrContainerRef = useRef(null)
  const qrElementRef = useRef(null)
  const isCreatingQrRef = useRef(false)
  const [qrCreated, setQrCreated] = React.useState(false)

  const hasUser = !!(userEmail && String(userEmail).trim())

  const MAX_RETRIES = 2
  const VALIDATION_TIMEOUT = 15000
  const RETRY_TIMEOUT = 5000

  const resetLoadingState = useCallback(() => {
    setShowTimeoutMessage(false)
    setRetryCount(0)
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
      loadingTimeoutRef.current = null
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
      validationTimeoutRef.current = null
    }
  }, [])

  const validateAIContent = useCallback(() => {
    if (!aiToolsElementRef.current) {
      console.warn('No AI Tools element to validate')
      return false
    }

    const isValid = validateAIToolsContent(aiToolsElementRef.current)

    if (isValid) {
      setAiToolsValidated(true)
      setIsLoading(false)
      return true
    } else {
      console.warn(
        'AI Tools content validation failed - attempting re-registration'
      )
      return false
    }
  }, [])

  const createAssistantElement = useCallback(
    async (useReregistration = false) => {
      if (!hasUser || !assistantEnabled) {
        return
      }

      if (
        !aiToolsContainerRef.current ||
        !isMountedRef.current ||
        isCreatingRef.current
      ) {
        return
      }

      isCreatingRef.current = true
      try {
        const existingElements =
          aiToolsContainerRef.current.querySelectorAll('vapp-aitools')
        existingElements.forEach(element => element.remove())

        const element = await createAIToolsElement({
          containerRef: aiToolsContainerRef,
          elementRef: aiToolsElementRef,
          isMountedRef: isMountedRef,
          forceRecreate: true,
          reregisterOnFailure: useReregistration,
          setElementCreated: created => {
            if (created && isMountedRef.current) {
              setAiToolsCreated(true)

              validationTimeoutRef.current = setTimeout(() => {
                const isValid = validateAIContent()
                if (!isValid && retryCount < MAX_RETRIES) {
                  handleRetry()
                } else if (!isValid) {
                  setIsLoading(false)
                  setShowTimeoutMessage(true)
                }
              }, 8000)
            }
          },
          attributes: {
            tooltype: 'aichat',
            get userid() {
              return identityRef.current.userId
            },
            get coursenum() {
              return identityRef.current.coursenum
            },
            pageid: pageId,
            isdrawer: 'true',
          },
          onSuccess: () => {
            console.log('AI Tools element creation success callback triggered')
          },
        })

        return element
      } catch (error) {
        console.error('Failed to create assistant element:', error)
        if (isMountedRef.current) {
          if (retryCount < MAX_RETRIES) {
            handleRetry()
          } else {
            setIsLoading(false)
            setShowTimeoutMessage(true)
          }
        }

        if (
          !aiToolsContainerRef.current ||
          !isMountedRef.current ||
          isCreatingRef.current
        ) {
          return
        }

        isCreatingRef.current = true
        try {
          const existingElements =
            aiToolsContainerRef.current.querySelectorAll('vapp-aitools')
          existingElements.forEach(element => element.remove())

          const element = await createAIToolsElement({
            containerRef: aiToolsContainerRef,
            elementRef: aiToolsElementRef,
            isMountedRef: isMountedRef,
            forceRecreate: true,
            reregisterOnFailure: useReregistration,
            setElementCreated: created => {
              if (created && isMountedRef.current) {
                setAiToolsCreated(true)

                validationTimeoutRef.current = setTimeout(() => {
                  const isValid = validateAIContent()
                  if (!isValid && retryCount < MAX_RETRIES) {
                    handleRetry()
                  } else if (!isValid) {
                    setIsLoading(false)
                    setShowTimeoutMessage(true)
                  }
                }, 8000)
              }
            },
            attributes: {
              tooltype: 'aichat',
              get userid() {
                return identityRef.current.userId
              },
              get coursenum() {
                return identityRef.current.coursenum
              },
              pageid: pageId,
              isdrawer: 'true',
            },
            onSuccess: () => {
              console.log(
                'AI Tools element creation success callback triggered'
              )
            },
          })

          return element
        } catch (error) {
          console.error('Failed to create assistant element:', error)
          if (isMountedRef.current) {
            if (retryCount < MAX_RETRIES) {
              handleRetry()
            } else {
              setIsLoading(false)
              setShowTimeoutMessage(true)
            }
          }
        } finally {
          isCreatingRef.current = false
        }
      }
    },
    [userId, coursenum, pageId, retryCount, validateAIContent, hasUser]
  )

  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES) {
      console.error('Max retries reached')
      setShowTimeoutMessage(true)
      setIsLoading(false)
      return
    }

    setRetryCount(prev => prev + 1)
    setAiToolsCreated(false)
    setAiToolsValidated(false)

    setTimeout(() => {
      if (isMountedRef.current) {
        createAssistantElement(retryCount >= 1)
      }
    }, RETRY_TIMEOUT)
  }, [retryCount, createAssistantElement])

  const createQuickReviewElement = useCallback(async () => {
    if (
      !qrContainerRef.current ||
      !isMountedRef.current ||
      isCreatingQrRef.current
    ) {
      return
    }
    isCreatingQrRef.current = true
    try {
      const existingElements =
        qrContainerRef.current.querySelectorAll('vapp-aitools')
      existingElements.forEach(el => el.remove())

      await createAIToolsElement({
        containerRef: qrContainerRef,
        elementRef: qrElementRef,
        isMountedRef: isMountedRef,
        forceRecreate: true,
        reregisterOnFailure: false,
        setElementCreated: created => {
          if (created && isMountedRef.current) {
            setQrCreated(true)
          }
        },
        attributes: {
          tooltype: 'quickreview',
          get userid() {
            return identityRef.current.userId
          },
          get coursenum() {
            return identityRef.current.coursenum
          },
          pageid: pageId,
          'recent-page-ids': JSON.stringify(recentPageIds || []),
          'upcoming-assessments': JSON.stringify(upcomingAssessments || []),
          isdrawer: 'true',
        },
        onSuccess: () => {},
      })
    } catch (error) {
      console.error('Failed to create Quick Review element:', error)
    } finally {
      isCreatingQrRef.current = false
    }
  }, [userId, coursenum, pageId, recentPageIds, upcomingAssessments])

  useEffect(() => {
    const handleDrawerStateChange = event => {
      const newActiveDrawer = event.detail.activeDrawer
      if (
        (newActiveDrawer === 'assistant' ||
          newActiveDrawer === 'quickreview') &&
        !assistantEnabled
      ) {
        console.warn('AI is disabled for this course')
        return
      }
      setActiveDrawer(newActiveDrawer)

      // Focus the assistant input when drawer opens
      if (newActiveDrawer === 'assistant' && hasUser && assistantEnabled) {
        setTimeout(() => {
          if (aiToolsContainerRef.current) {
            const aiToolsElement =
              aiToolsContainerRef.current.querySelector('vapp-aitools')
            if (aiToolsElement) {
              let chatInput = null
              if (aiToolsElement.shadowRoot) {
                chatInput = aiToolsElement.shadowRoot.querySelector(
                  'textarea.input-box, textarea[placeholder*="question"]'
                )
              } else {
                chatInput = aiToolsElement.querySelector(
                  'textarea.input-box, textarea[placeholder*="question"]'
                )
              }

              if (chatInput) {
                chatInput.focus()
              }
            }
          }
        }, 400)
      }
    }

    document.addEventListener('drawer-state-change', handleDrawerStateChange)

    return () => {
      document.removeEventListener(
        'drawer-state-change',
        handleDrawerStateChange
      )
    }
  }, [assistantEnabled, hasUser])

  useEffect(() => {
    if (!hasUser || !assistantEnabled) {
      setIsLoading(false)
      setShowTimeoutMessage(false)
      return
    }

    if (
      aiToolsContainerRef.current &&
      !aiToolsCreated &&
      !isCreatingRef.current
    ) {
      createAssistantElement(false)

      loadingTimeoutRef.current = setTimeout(() => {
        if (!aiToolsValidated && isMountedRef.current) {
          setShowTimeoutMessage(true)
          setIsLoading(false)
        }
      }, VALIDATION_TIMEOUT)
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [
    aiToolsCreated,
    createAssistantElement,
    hasUser,
    assistantEnabled,
    aiToolsValidated,
  ])

  // Logging out removes the tool containers; allow fresh mounts on login.
  useEffect(() => {
    if (!hasUser) {
      aiToolsElementRef.current?.remove()
      qrElementRef.current?.remove()
      aiToolsElementRef.current = null
      qrElementRef.current = null
      isCreatingRef.current = false
      isCreatingQrRef.current = false
      setAiToolsCreated(false)
      setQrCreated(false)
      setAiToolsValidated(false)
    }
  }, [hasUser])

  // Identity changes must reach already-created tools, including hidden drawers.
  useEffect(() => {
    for (const ref of [aiToolsElementRef, qrElementRef]) {
      if (ref.current) {
        ref.current.setAttribute('userid', userId)
        ref.current.setAttribute('coursenum', coursenum || '')
      }
    }
  }, [userId, coursenum, aiToolsCreated, qrCreated])

  useEffect(() => {
    if (aiToolsElementRef.current) {
      aiToolsElementRef.current.style.display =
        activeDrawer === 'assistant' && hasUser && assistantEnabled
          ? ''
          : 'none'
    }
  }, [activeDrawer, hasUser, assistantEnabled])

  useEffect(() => {
    if (activeDrawer === 'quickreview' && hasUser && assistantEnabled) {
      if (qrContainerRef.current && !qrCreated && !isCreatingQrRef.current) {
        createQuickReviewElement()
      }
    }
  }, [
    activeDrawer,
    qrCreated,
    createQuickReviewElement,
    hasUser,
    assistantEnabled,
  ])

  useEffect(() => {
    if (qrElementRef.current && pageId) {
      qrElementRef.current.setAttribute('pageid', pageId)
    }
  }, [pageId])

  useEffect(() => {
    if (qrElementRef.current) {
      qrElementRef.current.setAttribute(
        'recent-page-ids',
        JSON.stringify(recentPageIds || [])
      )
    }
  }, [recentPageIds])

  useEffect(() => {
    if (qrElementRef.current) {
      qrElementRef.current.setAttribute(
        'upcoming-assessments',
        JSON.stringify(upcomingAssessments || [])
      )
    }
  }, [upcomingAssessments])

  useEffect(() => {
    if (qrElementRef.current) {
      qrElementRef.current.style.display =
        activeDrawer === 'quickreview' && hasUser && assistantEnabled
          ? ''
          : 'none'
    }
  }, [activeDrawer, hasUser, assistantEnabled])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      resetLoadingState()

      if (aiToolsContainerRef.current) {
        const existingElements =
          aiToolsContainerRef.current.querySelectorAll('vapp-aitools')
        existingElements.forEach(element => element.remove())
      }

      if (aiToolsElementRef.current) {
        aiToolsElementRef.current.remove()
        aiToolsElementRef.current = null
      }

      if (qrContainerRef.current) {
        const existingElements =
          qrContainerRef.current.querySelectorAll('vapp-aitools')
        existingElements.forEach(el => el.remove())
      }

      if (qrElementRef.current) {
        qrElementRef.current.remove()
        qrElementRef.current = null
      }

      setAiToolsCreated(false)
      isCreatingRef.current = false
    }
  }, [resetLoadingState])

  return (
    <div className="drawer-content-wrapper">
      <div
        className={`drawer-content drawer-assistant ${
          activeDrawer === 'assistant' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'assistant'}
        inert={activeDrawer !== 'assistant' ? '' : undefined}
      >
        {!hasUser ? (
          <div className="drawer-empty-state">
            <DrawerHeader title={formatMessage('Virtual Assistant')} />
            <p className="text-muted text-p-margin-removed p-3">
              {formatMessage(
                'You must be logged in to use the virtual assistant.'
              )}
            </p>
          </div>
        ) : !assistantEnabled ? (
          <div className="drawer-empty-state">
            <DrawerHeader title={formatMessage('Virtual Assistant')} />
            <p className="text-muted text-p-margin-removed p-3">
              {formatMessage(
                'The AI assistant is not enabled for this course.'
              )}
            </p>
          </div>
        ) : (
          <div ref={aiToolsContainerRef} className="ai-tools-container">
            <div
              className={`chatbot-loader ${
                isLoading && !aiToolsValidated && activeDrawer === 'assistant'
                  ? 'always-visible'
                  : 'hidden'
              }`}
              aria-live="polite"
              aria-label="Loading assistant"
              style={{ zIndex: 5 }}
            >
              <div className="loader-content">
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                {!showTimeoutMessage ? (
                  <div>
                    <span>Loading assistant…</span>
                    {retryCount > 0 && (
                      <p>
                        <small>
                          Retry attempt {retryCount}/{MAX_RETRIES}
                        </small>
                      </p>
                    )}
                    {aiToolsCreated && !aiToolsValidated && (
                      <p>
                        <small>Validating content...</small>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="timeout-message">
                    <p>The assistant is having trouble loading.</p>
                    <p>Try refreshing the page:</p>
                    {(() => {
                      const userAgent = navigator.userAgent
                      if (/Macintosh|Mac OS X/i.test(userAgent)) {
                        return (
                          <p>
                            <strong>⌘ + Shift + R</strong> (Mac)
                          </p>
                        )
                      } else if (/Windows/i.test(userAgent)) {
                        return (
                          <p>
                            <strong>Ctrl + F5</strong> (Windows)
                          </p>
                        )
                      } else if (/Linux/i.test(userAgent)) {
                        return (
                          <p>
                            <strong>Ctrl + Shift + R</strong> (Linux)
                          </p>
                        )
                      } else {
                        return <p>Please refresh your browser</p>
                      }
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={`drawer-content drawer-assistant ${
          activeDrawer === 'quickreview' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'quickreview'}
        style={{ height: '100%' }}
      >
        {!hasUser ? (
          <div className="drawer-empty-state">
            <DrawerHeader title={formatMessage('Quick Review')} />
            <p className="text-muted text-p-margin-removed p-3">
              {formatMessage('You must be logged in to use Quick Review.')}
            </p>
          </div>
        ) : (
          <div
            className="drawer-base active d-flex flex-column"
            style={{ height: '100%' }}
          >
            <DrawerHeader title={formatMessage('Quick Review')} />
            <div
              ref={qrContainerRef}
              className="ai-tools-container"
              style={{ flex: 1, height: 'auto' }}
            >
              {!qrCreated && activeDrawer === 'quickreview' && (
                <div
                  className="chatbot-loader always-visible"
                  aria-live="polite"
                  aria-label="Loading Quick Review"
                  style={{ zIndex: 5 }}
                >
                  <div className="loader-content">
                    <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>Loading Quick Review…</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={`drawer-base ${
          activeDrawer === 'search' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'search'}
        inert={activeDrawer !== 'search' ? '' : undefined}
        style={{ height: '100%' }}
      >
        <DrawerSearch
          coursenum={coursenum}
          userId={userId}
          userEmail={userEmail}
          assistantEnabled={assistantEnabled}
        />
      </div>

      <div
        className={`drawer-base ${
          activeDrawer === 'glossary' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'glossary'}
        inert={activeDrawer !== 'glossary' ? '' : undefined}
        style={{ height: '100%' }}
      >
        {activeDrawer === 'glossary' && (
          <DrawerGlossary coursenum={coursenum} userEmail={userEmail} />
        )}
      </div>

      <div
        className={`drawer-base ${
          activeDrawer === 'settings' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'settings'}
        inert={activeDrawer !== 'settings' ? '' : undefined}
        style={{ height: '100%' }}
      >
        <DrawerSettings
          coursenum={coursenum}
          userEmail={userEmail}
          assistantEnabled={assistantEnabled}
          userId={userId}
        />
      </div>

      <div
        className={`drawer-base ${
          activeDrawer === 'notebook' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'notebook'}
        inert={activeDrawer !== 'notebook' ? '' : undefined}
        style={{ height: '100%' }}
      >
        {activeDrawer === 'notebook' && (
          <DrawerNotebook coursenum={coursenum} />
        )}
      </div>

      <div
        className={`drawer-base ${
          activeDrawer === 'contact' ? 'active' : 'hidden'
        }`}
        aria-hidden={activeDrawer !== 'contact'}
        inert={activeDrawer !== 'contact' ? '' : undefined}
        style={{ height: '100%' }}
      >
        <DrawerContactUs coursenum={coursenum} userEmail={userEmail} />
      </div>

      {activeDrawer &&
        ![
          'assistant',
          'quickreview',
          'search',
          'glossary',
          'notebook',
          'settings',
          'contact',
        ].includes(activeDrawer) && (
          <div className="drawer-content drawer-empty">
            <p>Select an option from the icon bar</p>
          </div>
        )}
    </div>
  )
}

DrawerContainer.propTypes = {
  userId: PropTypes.string,
  coursenum: PropTypes.string,
  pageId: PropTypes.string,
  recentPageIds: PropTypes.arrayOf(PropTypes.string),
  upcomingAssessments: PropTypes.arrayOf(
    PropTypes.shape({
      activity_id: PropTypes.string,
      guid: PropTypes.string,
      title: PropTypes.string,
      href: PropTypes.string,
      due_date: PropTypes.string,
      start_date: PropTypes.string,
      available_now: PropTypes.bool,
      scope_kind: PropTypes.string,
      page_ids: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  userEmail: PropTypes.string,
  assistantEnabled: PropTypes.bool,
}
